import React from "react";

import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import ColumnHeader from "../../components/common/ColumnHeader.jsx";
import ReadableCurrency from "../../components/ReadableCurrency.jsx";

const sortOptions = {
  defaultSort: {
    field: "year",
    reverse: true,
  },
};

function EngineeringProjectYearlySummary({
  // Props
  project,
}) {
  const theme = useTheme();

  const rawYearlyData = React.useMemo(() => {
    const summary = {};

    project.engineeringExpenses?.forEach((expense) => {
      const year = DateTime.fromISO(expense.date).year;
      if (isNaN(year)) {
        return;
      }
      if (!summary[year]) {
        summary[year] = { year, expenses: 0, hours: 0, cost: 0, employees: new Set() };
      }
      summary[year].expenses += Number(expense.amount) || 0;
    });

    project.projectWork?.forEach((work) => {
      const year = DateTime.fromISO(work.sprint?.endDate).year;
      if (isNaN(year)) {
        return;
      }
      if (!summary[year]) {
        summary[year] = { year, expenses: 0, hours: 0, cost: 0, employees: new Set() };
      }
      summary[year].hours += Number(work.hours) || 0;
      summary[year].cost += Number(work.cost) || 0;
      if (work.user?.name) {
        summary[year].employees.add(work.user.name);
      }
    });

    return Object.values(summary).map((s) => ({
      ...s,
      employees: Array.from(s.employees).toSorted().join(", ") || "None",
    }));
  }, [project.engineeringExpenses, project.projectWork]);

  const [sortedYearlyData, handleSortSelection, sort] = useSort(rawYearlyData, sortOptions);

  if (!sortedYearlyData.length) {
    return null;
  }

  return (
    <Box sx={{ mx: 2, mb: 4 }} data-cy="yearly-summary-section">
      <Grid container spacing={1}>
        <Grid size={12}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Yearly Summary</Typography>
          </Stack>
        </Grid>
        <Grid size={1.5}>
          <ColumnHeader
            id="year"
            display="Year"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={2.5}>
          <ColumnHeader
            id="expenses"
            display="Material Expenses"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={2}>
          <ColumnHeader
            id="hours"
            display="Employee Hours"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size={2}>
          <ColumnHeader
            id="cost"
            display="Labor Cost"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
        <Grid size="grow">
          <ColumnHeader
            id="employees"
            display="Employees"
            sortField={sort.field}
            reverseSort={sort.reverse}
            onClick={handleSortSelection}
          />
        </Grid>
      </Grid>
      <Divider variant="middle" color={theme.palette.primary.dark} sx={{ borderWidth: "1px", mx: -1 }} />
      {sortedYearlyData.map((data) => (
        <React.Fragment key={data.year}>
          <Grid
            container
            spacing={1}
            sx={{ py: 1, alignItems: "center" }}
            data-cy={`yearly-summary-row-${data.year}`}
          >
            <Grid size={1.5} data-cy="yearly-summary-year">
              <Typography>{data.year}</Typography>
            </Grid>
            <Grid size={2.5} data-cy="yearly-summary-expenses">
              <ReadableCurrency value={data.expenses} />
            </Grid>
            <Grid size={2} data-cy="yearly-summary-hours">
              <Typography>{data.hours.toFixed(2)}</Typography>
            </Grid>
            <Grid size={2} data-cy="yearly-summary-cost">
              <ReadableCurrency value={data.cost} />
            </Grid>
            <Grid size="grow" data-cy="yearly-summary-employees">
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                {data.employees}
              </Typography>
            </Grid>
          </Grid>
          <Divider variant="middle" sx={{ mx: -1 }} />
        </React.Fragment>
      ))}
    </Box>
  );
}

export default EngineeringProjectYearlySummary;
